/**
 * Content Bucket List Component
 * 
 * Displays content buckets in a list/table format as an alternative to the grid view.
 * 
 * Features:
 * - Compact list display
 * - Quick actions for each bucket
 * - Sortable columns
 * - Mobile-responsive
 * 
 * @author AI Assistant
 * @created 2024
 */

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  MoreVert as MoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  VisibilityOff as HideIcon,
  Lightbulb as IdeasIcon,
  Assignment as PostsIcon,
  Schedule as FrequencyIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

import { useContentBuckets } from '../providers/ContentBucketsProvider';
import ContentBucketForm from './ContentBucketForm';
import ContentIdeasDialog from './ContentIdeasDialog';
import ConfirmDialog from './ConfirmDialog';

const ColorIndicator = styled(Box)(({ bucketColor }) => ({
  width: 16,
  height: 16,
  borderRadius: '50%',
  backgroundColor: bucketColor,
  border: '2px solid #fff',
  boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
  flexShrink: 0
}));

const StyledTableRow = styled(TableRow)(({ theme, isActive }) => ({
  opacity: isActive ? 1 : 0.7,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    '& .bucket-actions': {
      opacity: 1
    }
  },
  '& .bucket-actions': {
    opacity: 0,
    transition: 'opacity 0.3s ease'
  }
}));

const ContentBucketList = ({ buckets }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { updateBucket, deleteBucket } = useContentBuckets();

  // Local state for menus and dialogs
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedBucket, setSelectedBucket] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showIdeasDialog, setShowIdeasDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const menuOpen = Boolean(anchorEl);

  // Format frequency display
  const getFrequencyText = (bucket) => {
    const frequencyMap = {
      daily: 'Daily',
      weekly: 'Weekly',
      bi_weekly: 'Bi-weekly',
      monthly: 'Monthly',
      custom: `Every ${bucket.frequency_days} days`
    };
    return frequencyMap[bucket.target_frequency] || 'Weekly';
  };

  // Format template display
  const getTemplateText = (bucket) => {
    return bucket.industry_template
      ?.replace('_', ' ')
      ?.replace(/\b\w/g, l => l.toUpperCase()) || 'Custom';
  };

  // Handle menu
  const handleMenuOpen = (event, bucket) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedBucket(bucket);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedBucket(null);
  };

  // Handle toggle active status
  const handleToggleActive = async () => {
    if (!selectedBucket) return;
    
    try {
      await updateBucket(selectedBucket.id, {
        ...selectedBucket,
        is_active: !selectedBucket.is_active
      });
      handleMenuClose();
    } catch (error) {
      console.error('Failed to toggle bucket status:', error);
    }
  };

  // Handle edit
  const handleEdit = () => {
    setShowEditForm(true);
    handleMenuClose();
  };

  // Handle view ideas
  const handleViewIdeas = () => {
    setShowIdeasDialog(true);
    handleMenuClose();
  };

  // Handle delete
  const handleDelete = () => {
    setShowDeleteConfirm(true);
    handleMenuClose();
  };

  // Confirm delete
  const handleConfirmDelete = async () => {
    if (!selectedBucket) return;
    
    setIsDeleting(true);
    try {
      await deleteBucket(selectedBucket.id);
      setShowDeleteConfirm(false);
      setSelectedBucket(null);
    } catch (error) {
      console.error('Failed to delete bucket:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle form success
  const handleEditSuccess = () => {
    setShowEditForm(false);
    setSelectedBucket(null);
  };

  if (isMobile) {
    // Mobile card view for better UX on small screens
    return (
      <Box>
        {buckets.map((bucket) => (
          <Paper key={bucket.id} sx={{ mb: 2, p: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <ColorIndicator bucketColor={bucket.color} />
                <Typography variant="h6" sx={{ fontSize: '1rem' }}>
                  {bucket.name}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={(e) => handleMenuOpen(e, bucket)}
              >
                <MoreIcon fontSize="small" />
              </IconButton>
            </Box>
            
            <Box display="flex" gap={1} mb={1} flexWrap="wrap">
              <Chip
                label={bucket.is_active ? 'Active' : 'Inactive'}
                size="small"
                color={bucket.is_active ? 'success' : 'default'}
                variant={bucket.is_active ? 'filled' : 'outlined'}
              />
              <Chip
                label={getTemplateText(bucket)}
                size="small"
                variant="outlined"
              />
            </Box>
            
            <Box display="flex" gap={2} flexWrap="wrap">
              <Box display="flex" alignItems="center" gap={0.5}>
                <PostsIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {bucket.post_count || 0} posts
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={0.5}>
                <FrequencyIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {getFrequencyText(bucket)}
                </Typography>
              </Box>
            </Box>
          </Paper>
        ))}
      </Box>
    );
  }

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Bucket</TableCell>
              <TableCell>Template</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Posts</TableCell>
              <TableCell>Frequency</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {buckets.map((bucket) => (
              <StyledTableRow key={bucket.id} isActive={bucket.is_active}>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <ColorIndicator bucketColor={bucket.color} />
                    <Box>
                      <Typography variant="body1" fontWeight={500}>
                        {bucket.name}
                      </Typography>
                      {bucket.description && (
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: 200
                          }}
                        >
                          {bucket.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </TableCell>
                
                <TableCell>
                  <Typography variant="body2">
                    {getTemplateText(bucket)}
                  </Typography>
                </TableCell>
                
                <TableCell>
                  <Chip
                    label={bucket.is_active ? 'Active' : 'Inactive'}
                    size="small"
                    color={bucket.is_active ? 'success' : 'default'}
                    variant={bucket.is_active ? 'filled' : 'outlined'}
                  />
                </TableCell>
                
                <TableCell align="center">
                  <Typography variant="body2">
                    {bucket.post_count || 0}
                  </Typography>
                </TableCell>
                
                <TableCell>
                  <Typography variant="body2">
                    {getFrequencyText(bucket)}
                  </Typography>
                </TableCell>
                
                <TableCell align="center">
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, bucket)}
                    className="bucket-actions"
                  >
                    <MoreIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          elevation: 8,
          sx: { minWidth: 180 }
        }}
      >
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Bucket</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={handleToggleActive}>
          <ListItemIcon>
            {selectedBucket?.is_active ? <HideIcon fontSize="small" /> : <ViewIcon fontSize="small" />}
          </ListItemIcon>
          <ListItemText>
            {selectedBucket?.is_active ? 'Deactivate' : 'Activate'}
          </ListItemText>
        </MenuItem>

        <MenuItem onClick={handleViewIdeas}>
          <ListItemIcon>
            <IdeasIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Content Ideas</ListItemText>
        </MenuItem>

        <Divider />

        <MenuItem 
          onClick={handleDelete}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete Bucket</ListItemText>
        </MenuItem>
      </Menu>

      {/* Edit Form Dialog */}
      {selectedBucket && (
        <ContentBucketForm
          open={showEditForm}
          onClose={() => setShowEditForm(false)}
          onSuccess={handleEditSuccess}
          bucket={selectedBucket}
          mode="edit"
        />
      )}

      {/* Content Ideas Dialog */}
      {selectedBucket && (
        <ContentIdeasDialog
          open={showIdeasDialog}
          onClose={() => setShowIdeasDialog(false)}
          bucket={selectedBucket}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {selectedBucket && (
        <ConfirmDialog
          open={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleConfirmDelete}
          title="Delete Content Bucket"
          message={
            <>
              Are you sure you want to delete the bucket <strong>"{selectedBucket.name}"</strong>?
              {selectedBucket.post_count > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="warning.main">
                    ⚠️ This bucket contains {selectedBucket.post_count} post{selectedBucket.post_count !== 1 ? 's' : ''}. 
                    You'll need to remove all posts before deletion.
                  </Typography>
                </Box>
              )}
            </>
          }
          confirmText="Delete"
          confirmColor="error"
          loading={isDeleting}
          disabled={selectedBucket.post_count > 0}
        />
      )}
    </>
  );
};

export default ContentBucketList; 